import type { EChartsOption } from 'echarts';
import type {
    SplitDistributionParticipant,
    SplitDistributionSplit,
} from '../../services/graphApi';

type ParticipantChartRow = {
    bibNumber: number;
    name?: string;
    segments: number[];
    totalSeconds: number;
};

const splitColors = [
    '#1cab1c',
    '#1124ff',
    '#fff100',
    '#ffb000',
    '#9321b3',
    '#36f3ff',
    '#ff38ff',
    '#7e2f2f',
    '#1eff1e',
    '#20c4d8',
    '#5b1095',
];

function getRecordTime(record: { chiptimeElapsedTime?: string; guntimeElapsedTime?: string }) {
    return record.chiptimeElapsedTime ?? record.guntimeElapsedTime ?? '';
}

function parseTimeToSeconds(time: string) {
    const [hour = '0', minute = '0', second = '0'] = time.split(':');
    const parsedSecond = Number(second);
    const seconds = Math.floor(Number.isFinite(parsedSecond) ? parsedSecond : 0);

    return Number(hour) * 3600 + Number(minute) * 60 + seconds;
}

function formatSeconds(totalSeconds: number) {
    const hour = Math.floor(totalSeconds / 3600);
    const minute = Math.floor((totalSeconds % 3600) / 60);
    const second = Math.floor(totalSeconds % 60);

    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
}

function getSplitElapsedSeconds(
    participant: SplitDistributionParticipant,
    split: SplitDistributionSplit
) {
    const record = participant.records.find((candidate) => candidate.splitId === split.splitId);
    const recordTime = record ? getRecordTime(record) : '';

    return recordTime ? parseTimeToSeconds(recordTime) : null;
}

function toParticipantChartRow(
    participant: SplitDistributionParticipant,
    splits: SplitDistributionSplit[]
): ParticipantChartRow {
    let previousElapsedSeconds = 0;
    const segments = splits.map((split) => {
        const elapsedSeconds = getSplitElapsedSeconds(participant, split);

        if (elapsedSeconds === null) {
            return 0;
        }

        const segmentSeconds = Math.max(elapsedSeconds - previousElapsedSeconds, 0);
        previousElapsedSeconds = elapsedSeconds;

        return segmentSeconds;
    });

    return {
        bibNumber: participant.bibNumber,
        name: participant.name,
        segments,
        totalSeconds: segments.reduce((sum, value) => sum + value, 0),
    };
}

function roundUpToThirtyMinutes(seconds: number) {
    const thirtyMinutes = 30 * 60;

    return Math.max(thirtyMinutes, Math.ceil(seconds / thirtyMinutes) * thirtyMinutes);
}

export function createSplitDistributionOption(
    splits: SplitDistributionSplit[],
    participants: SplitDistributionParticipant[],
    highlightedParticipantIndex: number | null = null,
    visibleStartIndex = 0,
    visibleParticipantCount = 121,
    categoryId = 0
): EChartsOption {
    const rows = participants.map((participant) => toParticipantChartRow(participant, splits));
    const maxTotalSeconds = Math.max(...rows.map((row) => row.totalSeconds), 0);
    const yAxisLabels = rows.map((row) => String(row.bibNumber));
    const lastParticipantIndex = Math.max(participants.length - 1, 0);
    const dataZoomStartValue = Math.min(Math.max(visibleStartIndex, 0), lastParticipantIndex);
    const dataZoomEndValue = Math.min(
        lastParticipantIndex,
        dataZoomStartValue + Math.max(visibleParticipantCount - 1, 0)
    );

    const categoryColorOffset = Math.abs(categoryId) % splitColors.length;
    const getSplitColor = (splitIndex: number) =>
        splitColors[(categoryColorOffset + splitIndex) % splitColors.length];

    const series = splits.map((split, splitIndex) => ({
        name: split.splitName,
        type: 'bar' as const,
        stack: 'total',
        barWidth: 4,
        itemStyle: {
            color: getSplitColor(splitIndex),
            borderRadius: splitIndex === splits.length - 1 ? [0, 4, 4, 0] : 0,
        },
        data: rows.map((row, rowIndex) => {
            const isHighlighted = highlightedParticipantIndex === rowIndex;
            const hasHighlightedRow = highlightedParticipantIndex !== null;

            return {
                value: row.segments[splitIndex],
                itemStyle: {
                    color: getSplitColor(splitIndex),
                    opacity: !hasHighlightedRow || isHighlighted ? 1 : 0.22,
                    shadowBlur: isHighlighted ? 10 : 0,
                    shadowColor: isHighlighted ? 'rgba(17, 24, 39, 0.35)' : 'transparent',
                    borderColor: isHighlighted ? '#111827' : 'transparent',
                    borderWidth: isHighlighted ? 1 : 0,
                    borderRadius: splitIndex === splits.length - 1 ? [0, 4, 4, 0] : 0,
                },
            };
        }),
    }));

    return {
        backgroundColor: '#ffffff',
        animation: false,
        color: splits.map((_, splitIndex) => getSplitColor(splitIndex)),
        grid: {
            top: 54,
            left: 28,
            right: 132,
            bottom: 22,
            containLabel: true,
        },
        legend: {
            type: 'scroll',
            orient: 'vertical',
            right: 26,
            top: 60,
            bottom: 18,
            itemWidth: 14,
            itemHeight: 14,
            textStyle: {
                color: '#3f3f46',
                fontSize: 12,
            },
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow',
            },
            formatter: (params) => {
                const items = Array.isArray(params) ? params : [params];
                const rowIndex = Number((items[0] as { dataIndex?: number } | undefined)?.dataIndex ?? 0);
                const row = rows[rowIndex];

                if (!row) {
                    return '';
                }

                const segmentLines = items
                    .map((item) => {
                        const chartItem = item as { marker?: string; seriesName?: string; value?: number };
                        const value = Number(chartItem.value);

                        if (!Number.isFinite(value) || value <= 0) {
                            return '';
                        }

                        return `${chartItem.marker ?? ''}${chartItem.seriesName ?? ''}: ${formatSeconds(value)}`;
                    })
                    .filter(Boolean);

                return [
                    `<strong>배번호 ${row.bibNumber}</strong>`,
                    row.name ? `이름: ${row.name}` : '',
                    `Finish ${formatSeconds(row.totalSeconds)}`,
                    ...segmentLines,
                ].filter(Boolean).join('<br/>');
            },
        },
        xAxis: {
            type: 'value',
            min: 0,
            max: roundUpToThirtyMinutes(maxTotalSeconds),
            position: 'top',
            interval: 30 * 60,
            axisLabel: {
                color: '#5b5b5b',
                formatter: (value: number) => formatSeconds(value),
            },
            axisLine: {
                lineStyle: {
                    color: '#c7c7c7',
                },
            },
            splitLine: {
                show: true,
                lineStyle: {
                    color: '#9f9f9f',
                    type: 'dashed',
                },
            },
        },
        yAxis: {
            type: 'category',
            inverse: true,
            data: yAxisLabels,
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
            axisLabel: {
                color: '#484848',
                interval: participants.length > 200 ? 49 : 0,
                margin: 6,
                fontSize: 11,
            },
        },
        dataZoom: [
            {
                type: 'inside',
                yAxisIndex: 0,
                startValue: dataZoomStartValue,
                endValue: dataZoomEndValue,
                zoomOnMouseWheel: false,
                moveOnMouseMove: true,
                moveOnMouseWheel: true,
            },
            {
                type: 'slider',
                yAxisIndex: 0,
                right: 4,
                width: 14,
                top: 96,
                bottom: 24,
                startValue: dataZoomStartValue,
                endValue: dataZoomEndValue,
                zoomLock: true,
                brushSelect: false,
                showDataShadow: false,
                fillerColor: 'rgba(37, 99, 235, 0.2)',
                borderColor: '#d6d6d6',
                handleSize: 0,
                showDetail: false,
            },
        ],
        series,
    };
}
